/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useState, useEffect } from 'react';
import { MessageCircle, Image, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import PersonaSelector from '../components/PersonaSelector';
import PersonaChat from '../components/PersonaChat';
import ContentReference from '../components/ContentReference';
import DateFilter from '../components/DateFilter';
import { useDateFilter } from '../contexts/DateFilterContext';
import { isDemoMode } from '../services';

interface Persona {
  id: string;
  name: string;
  title: string;
  age: string;
  gender: string;
  city: string;
  occupation: string;
  avatar: string;
  description: string;
  personality_traits: string[];
  brand_preferences: {
    top_brands: string[];
    trust_factors: string[];
    content_preferences: string[];
  };
}

/**
 * SegmentsPage - Consumer Personas page
 * Redesigned with tabs for Chat and Content Analysis
 */
const SegmentsPage = () => {
  const { selectedDate, formatDate } = useDateFilter();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null);
  const [activeTab, setActiveTab] = useState<'chat' | 'content'>('chat');
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPersonas();
  }, []);

  const fetchPersonas = async () => {
    try {
      if (isDemoMode) {
        // Mock personas for demo mode
        const mockPersonas: Persona[] = [
          {
            id: 'persona-1',
            name: 'Ahmed Al-Rashid',
            title: 'Young Professional',
            age: '28',
            gender: 'Male',
            city: 'Dubai',
            occupation: 'Marketing Manager',
            avatar: '',
            description: 'Tech-savvy millennial who values quality and brand authenticity. Active on social media and influences purchasing decisions in his social circle.',
            personality_traits: ['Ambitious', 'Health-conscious', 'Trend-aware', 'Social'],
            brand_preferences: {
              top_brands: ['Coca-Cola', 'Pepsi', 'Red Bull', 'Sprite'],
              trust_factors: ['Quality', 'Brand heritage', 'Social proof'],
              content_preferences: ['Video ads', 'Influencer content', 'Social media']
            }
          },
          {
            id: 'persona-2',
            name: 'Fatima Hassan',
            title: 'Family Decision Maker',
            age: '35',
            gender: 'Female',
            city: 'Abu Dhabi',
            occupation: 'Teacher',
            avatar: '',
            description: 'Health-conscious mother who prioritizes family wellness. Makes most household purchasing decisions and researches products thoroughly.',
            personality_traits: ['Caring', 'Practical', 'Value-conscious', 'Research-oriented'],
            brand_preferences: {
              top_brands: ['Sprite', 'Fanta', 'Coca-Cola', 'Mountain Dew'],
              trust_factors: ['Health benefits', 'Family-friendly', 'Value for money'],
              content_preferences: ['Family content', 'Health tips', 'Reviews']
            }
          },
          {
            id: 'persona-3',
            name: 'Omar Khalil',
            title: 'Gen Z Trendsetter',
            age: '22',
            gender: 'Male',
            city: 'Sharjah',
            occupation: 'University Student',
            avatar: '',
            description: 'Digital native who discovers brands through social media. Influenced by peers and seeks unique, Instagram-worthy experiences.',
            personality_traits: ['Creative', 'Adventurous', 'Socially-connected', 'Experimental'],
            brand_preferences: {
              top_brands: ['Red Bull', 'Monster', 'Mountain Dew', 'Pepsi'],
              trust_factors: ['Cool factor', 'Peer recommendations', 'Unique experiences'],
              content_preferences: ['TikTok', 'Instagram Reels', 'Memes']
            }
          },
          {
            id: 'persona-4',
            name: 'Sarah Al-Mansoori',
            title: 'Health Enthusiast',
            age: '31',
            gender: 'Female',
            city: 'Dubai',
            occupation: 'Fitness Instructor',
            avatar: '',
            description: 'Fitness-focused professional who scrutinizes nutritional content. Brand ambassador potential with strong social media following.',
            personality_traits: ['Disciplined', 'Influential', 'Health-focused', 'Authentic'],
            brand_preferences: {
              top_brands: ['Coca-Cola Zero', 'Sprite Zero', 'Vitamin Water', 'Gatorade'],
              trust_factors: ['Low sugar', 'Natural ingredients', 'Sports endorsements'],
              content_preferences: ['Fitness content', 'Nutrition facts', 'Athlete stories']
            }
          },
          {
            id: 'persona-5',
            name: 'Khalid Mohammed',
            title: 'Business Executive',
            age: '45',
            gender: 'Male',
            city: 'Dubai',
            occupation: 'CEO',
            avatar: '',
            description: 'High-income professional with premium brand preferences. Values convenience and quality over price. Loyal to trusted brands.',
            personality_traits: ['Professional', 'Quality-focused', 'Time-conscious', 'Loyal'],
            brand_preferences: {
              top_brands: ['Coca-Cola', 'Perrier', 'San Pellegrino', 'Evian'],
              trust_factors: ['Premium quality', 'Brand prestige', 'Consistency'],
              content_preferences: ['Business publications', 'Premium events', 'LinkedIn']
            }
          }
        ];
        setPersonas(mockPersonas);
      } else {
        const response = await axios.get('/api/personas');
        setPersonas(response.data);
      }
    } catch (error) {
      console.error('Error fetching personas:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPersona = (persona: Persona) => {
    setSelectedPersona(persona);
    setActiveTab('chat');
    // Set first brand as default for content analysis
    if (persona.brand_preferences?.top_brands?.length > 0) {
      setSelectedBrand(persona.brand_preferences.top_brands[0]);
    }
  };

  const handleBack = () => {
    setSelectedPersona(null);
  };

  if (isLoading) {
    return (
      <div className="h-full w-full bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-text-muted mt-4">Loading personas...</p>
        </div>
      </div>
    );
  }

  // Detail view when persona is selected
  if (selectedPersona) {
    return (
      <div className="h-full w-full bg-background overflow-auto">
        <div className="p-6">
          {/* Header with back button and persona info */}
          <div className="mb-6">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-text-muted hover:text-foreground transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back to Personas</span>
            </button>

            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                <span className="text-xl font-semibold text-primary">
                  {selectedPersona.name.charAt(0)}
                </span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">{selectedPersona.name}</h1>
                <p className="text-text-secondary">{selectedPersona.title} • {selectedPersona.city}</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 mb-6 border-b border-border w-fit">
            <button
              onClick={() => setActiveTab('chat')}
              className={`flex items-center gap-2 px-4 py-3 transition-all text-sm font-medium relative ${
                activeTab === 'chat'
                  ? 'text-primary'
                  : 'text-text-muted hover:text-foreground'
              }`}
            >
              <MessageCircle className="w-4 h-4" />
              Chat with {selectedPersona.name.split(' ')[0]}
              {activeTab === 'chat' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('content')}
              className={`flex items-center gap-2 px-4 py-3 transition-all text-sm font-medium relative ${
                activeTab === 'content'
                  ? 'text-primary'
                  : 'text-text-muted hover:text-foreground'
              }`}
            >
              <Image className="w-4 h-4" />
              Content Analysis
              {activeTab === 'content' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
              )}
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'chat' ? (
            <div className="h-[600px]">
              <PersonaChat persona={selectedPersona} brandContext={selectedBrand} />
            </div>
          ) : (
            <div>
              {/* Brand selector for content analysis */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-text-muted mb-2">
                  Select a Brand to Analyze
                </label>
                <select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  className="w-full md:w-64 bg-background-secondary text-foreground rounded-lg px-4 py-2.5 border border-border focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Choose a brand...</option>
                  {selectedPersona.brand_preferences?.top_brands?.map((brand) => (
                    <option key={brand} value={brand}>
                      {brand}
                    </option>
                  ))}
                </select>
              </div>

              <ContentReference
                personaId={selectedPersona.id}
                personaName={selectedPersona.name}
                brand={selectedBrand}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Persona selector view
  return (
    <div className="h-full w-full bg-background overflow-auto">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Consumer Personas</h1>
              <p className="text-text-secondary">
                Chat with data-driven personas based on real survey demographics
              </p>
            </div>
            <DateFilter />
          </div>
        </div>

        {/* Persona selector */}
        <PersonaSelector
          personas={personas}
          selectedPersona={selectedPersona}
          onSelectPersona={handleSelectPersona}
        />
      </div>
    </div>
  );
};

export default SegmentsPage;
